import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle} from '@fortawesome/fontawesome-pro-solid';

import {orderData} from 'libs/salmon-utils';
import {ToolTipCell} from 'components/ToolTipCell';
import MultiSelect from 'components/MultiSelect';
import {sortProducts} from 'components/Coverage/table-formatter';
import {getSettings} from 'selectors/settings';
import {restrictPayers, extractPbms, getFieldNameKey} from './utils';
import DynamicFilter from './dynamic';
import {getSelectedFiltersFromUrl} from './url';

export const CoverageFilters = ({
  usStates,
  payers,
  planTypes,
  indications,
  products,
  pbms,
  selectedStates,
  selectedPayers,
  selectedIndications,
  selectedPlanTypes,
  selectedProducts,
  selectedPbms,
  selectedDynamicFilters,
  coveragePositions,
  inProgress,
  settings,
  styleSheet,
  urlParameters,
  onUrlFiltersUpdate,
  onFilterChange,
}) => {
  // Track previous filter data to detect changes
  const prevFilterDataRef = useRef({
    usStates,
    payers,
    planTypes,
    indications,
    products,
    pbms,
    coveragePositions,
  });

  // Effect to handle URL parameter parsing when filter data loads
  useEffect(() => {
    // Check if filter data has changed (loaded)
    const prevFilterData = prevFilterDataRef.current;
    let filtersChanged = false;

    // Compare all filter data from Redux
    if (JSON.stringify(usStates) !== JSON.stringify(prevFilterData.usStates) ||
        JSON.stringify(payers) !== JSON.stringify(prevFilterData.payers) ||
        JSON.stringify(planTypes) !== JSON.stringify(prevFilterData.planTypes) ||
        JSON.stringify(indications) !== JSON.stringify(prevFilterData.indications) ||
        JSON.stringify(products) !== JSON.stringify(prevFilterData.products) ||
        JSON.stringify(pbms) !== JSON.stringify(prevFilterData.pbms) ||
        JSON.stringify(coveragePositions) !== JSON.stringify(prevFilterData.coveragePositions)) {
      filtersChanged = true;
    }

    // Only proceed if filters changed AND URL parameters exist
    if (!filtersChanged || !urlParameters || Object.keys(urlParameters).length === 0) {
      // Update ref for next comparison
      prevFilterDataRef.current = {
        usStates,
        payers,
        planTypes,
        indications,
        products,
        pbms,
        coveragePositions,
      };
      return;
    }

    // Check if dynamic filters are enabled and if coveragePositions is needed
    const hasDynamicFilters = settings.filterLayout && settings.filterLayout.some((f) => f.filter === 'dynamic');
    const needsCoveragePositions = hasDynamicFilters && settings.table.dynamicColumns;

    // Check which filter types are present in URL parameters
    const urlFilterTypes = new Set();
    if (urlParameters && typeof urlParameters === 'object' && urlParameters !== null) {
      // Check for static filters in URL
      for (let filter of settings.filterLayout) {
        if (filter.filter !== 'dynamic') {
          const filterAlias = settings.urlParametersMap[filter.filter]?.alias || filter.filter;
          if (Object.prototype.hasOwnProperty.call(urlParameters, filterAlias)) {
            urlFilterTypes.add(filter.filter);
          }
        }
      }

      // Check for dynamic filters in URL
      if (hasDynamicFilters) {
        for (let filter of settings.filterLayout) {
          if (filter.filter === 'dynamic') {
            const fieldName = filter.fieldName;
            const fieldNameKey = getFieldNameKey(fieldName);
            const possibleKeys = [
              settings.urlParametersMap[fieldNameKey]?.alias || fieldNameKey,
              fieldNameKey,
              fieldName,
              settings.urlParametersMap[fieldName]?.alias || fieldName,
            ];

            if (possibleKeys.some((key) => Object.prototype.hasOwnProperty.call(urlParameters, key))) {
              urlFilterTypes.add('dynamic');
              break;
            }
          }
        }
      }
    }

    // Verify only required filter data is loaded (only for filters present in URL)
    let allFiltersLoaded = true;
    const urlParsingProps = {
      usStates,
      payers,
      planTypes,
      indications,
      products,
      pbms,
      settings,
    };

    // Only check static filters that are in the URL
    for (let filter of settings.filterLayout) {
      if (filter.filter !== 'dynamic' && urlFilterTypes.has(filter.filter)) {
        const filterPropString = (filter.filter === 'state')
          ? 'usStates'
          : (filter.filter === 'plan')
            ? 'planTypes'
            : `${filter.filter}s`;

        const filterData = urlParsingProps[filterPropString];
        if (!filterData || !Array.isArray(filterData) || filterData.length === 0) {
          allFiltersLoaded = false;
          break;
        }
      }
    }

    // If dynamic filters are in URL, ensure coveragePositions is loaded
    if (urlFilterTypes.has('dynamic') && needsCoveragePositions && (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0)) {
      allFiltersLoaded = false;
    }

    // If no filters in URL, we're done
    if (urlFilterTypes.size === 0) {
      allFiltersLoaded = false;
    }

    // If all filters loaded, parse URL and update parent
    if (allFiltersLoaded && onUrlFiltersUpdate) {
      const selectedFilters = getSelectedFiltersFromUrl(
        urlParameters,
        settings.filterLayout,
        urlParsingProps,
        coveragePositions
      );

      // Only call if we have filters to apply
      if (Object.keys(selectedFilters).length > 0) {
        onUrlFiltersUpdate(selectedFilters);
      }
    }

    // Update ref for next comparison
    prevFilterDataRef.current = {
      usStates,
      payers,
      planTypes,
      indications,
      products,
      pbms,
      coveragePositions,
    };
  }, [usStates, payers, planTypes, indications, products, pbms, coveragePositions, urlParameters, settings, onUrlFiltersUpdate]);

  // Internal handleChange function that calls onFilterChange callback
  const handleFilterChange = (selected, type, singleSelect) => {
    // eslint-disable-next-line
    switch (type) {
      case 'payers':
        onFilterChange({selectedPayers: selected});
        break;
      case 'usStates':
        onFilterChange({selectedStates: selected});
        break;
      case 'indications':
        onFilterChange({selectedIndications: selected});
        break;
      case 'planTypes':
        onFilterChange({selectedPlanTypes: selected});
        break;
      case 'products':
        // Disallow multiple select if singleSelect is true
        if (singleSelect && selected.length > 1) {
          onFilterChange({selectedProducts: selected.slice(-1)});
        } else {
          onFilterChange({selectedProducts: selected});
        }
        break;
      case 'pbms':
        onFilterChange({selectedPbms: selected});
        break;
      default:
        break;
    }
  };

  const multiSelectFilter = (column, options, selected, type, additionalProps = {}) => {
    // Create a wrapper function that captures singleSelect from column
    const createHandleChange = (type) => (selected) => {
      handleFilterChange(selected, type, column.singleSelect);
    };
    return (
      <Col key={column.filter} sm={column.gridWidth} className={`offset-sm-${column.offset}`}>
        <MultiSelect
          label={additionalProps.label || column.label}
          placeholder={column.placeholder}
          options={options}
          selected={selected}
          isFetching={inProgress}
          handleChange={createHandleChange(type)}
          selectHintOnEnter={false}
          type={type}
          {...additionalProps}
        />
      </Col>
    );
  };

  return (
    <Row key="filterInputs">
      {settings.filterLayout.map((column) => {
        // Handle dynamic filter type using DynamicFilter component
        if (column.filter === 'dynamic' && settings.table.dynamicColumns && coveragePositions) {
          const fieldName = column.fieldName;
          return (
            <DynamicFilter
              key={`dynamic-${getFieldNameKey(fieldName)}`}
              column={column}
              coveragePositions={coveragePositions}
              selectedDynamicFilters={selectedDynamicFilters}
              onFilterChange={onFilterChange}
              inProgress={inProgress}
              settings={settings}
              styleSheet={styleSheet}
            />
          );
        }

        switch (column.filter) {
          case 'state':
            return multiSelectFilter(column, usStates, selectedStates, 'usStates');
          case 'payer':
            return multiSelectFilter(column, payers, selectedPayers, 'payers');
          case 'plan': {
            const displayedPlanTypes = settings.displayedPlanTypes.length
              ? settings.displayedPlanTypes.map((planType) => {
                return {id: planType.ids.join(','), ...planType};
              })
              : planTypes;
            const planLabel = settings.content.planTypeHelpHover.length
              ? <span>{column.label}
                <ToolTipCell
                  text={settings.content.planTypeHelpHover}
                  placeholder={(<FontAwesomeIcon icon={faInfoCircle}/>)}
                  placement={'bottom'}
                  styleSheet={styleSheet}
                  id={'plantype-tooltip'}
                />
              </span>
              : column.label;
            return multiSelectFilter(
              column,
              displayedPlanTypes,
              selectedPlanTypes,
              'planTypes',
              {
                label: planLabel,
                hasChildren: column.hasChildren,
                dataTestId: 'plan-type-multiselect',
              }
            );
          }
          case 'indication':
            return multiSelectFilter(
              column,
              orderData(indications, 'id', column.orderByIds || []),
              selectedIndications,
              'indications'
            );
          case 'product':
            return multiSelectFilter(
              column,
              sortProducts(products, settings.drugId),
              selectedProducts,
              'products'
            );
          case 'pbm':
            return multiSelectFilter(column, pbms, selectedPbms, 'pbms');
          default:
            return null;
        }
      })}
    </Row>
  );
};

CoverageFilters.propTypes = {
  usStates: PropTypes.array,
  payers: PropTypes.array,
  planTypes: PropTypes.array,
  indications: PropTypes.array,
  products: PropTypes.array,
  pbms: PropTypes.array,
  selectedStates: PropTypes.array.isRequired,
  selectedPayers: PropTypes.array.isRequired,
  selectedIndications: PropTypes.array.isRequired,
  selectedPlanTypes: PropTypes.array.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  selectedPbms: PropTypes.array.isRequired,
  selectedDynamicFilters: PropTypes.object,
  coveragePositions: PropTypes.array,
  inProgress: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  urlParameters: PropTypes.object,
  onUrlFiltersUpdate: PropTypes.func,
  onFilterChange: PropTypes.func.isRequired,
};

const mapStateToProps = function (state) {
  const {coverage} = state;
  const settings = getSettings(state);
  const coveragePositions = coverage.get('coveragePositions').toJS();

  const result = {
    payers: restrictPayers(coverage, settings),
    products: coverage.get('products').toJS(),
    pbms: extractPbms(coveragePositions),
    usStates: coverage.get('usStates').toJS(),
    indications: coverage.get('indications').toJS(),
  };

  // Only add coveragePositions if dynamicColumns enabled
  if (settings.table.dynamicColumns) {
    result.coveragePositions = coveragePositions;
  }

  return result;
};

export default connect(mapStateToProps)(CoverageFilters);

/**
 * Computes filtered values from selected values based on visible columns.
 * This helper function allows us to derive filteredX from selectedX without
 * storing them separately in state.
 *
 * @param {Object} selectedValues - Object containing selectedX arrays
 * @param {Array} visibleColumns - Array of visible column property names
 * @param {Array} planTypes - Array of plan types (needed for planTypes filtering)
 * @returns {Object} Object containing filteredX arrays
 */
export const getFilteredValues = (selectedValues, visibleColumns, planTypes) => {
  const {
    selectedPayers,
    selectedStates,
    selectedIndications,
    selectedPlanTypes,
    selectedProducts,
    selectedPbms,
  } = selectedValues;

  // Filter planTypes based on selectedPlanTypes and visible columns
  const filteredPlanTypes = selectedPlanTypes.reduce((acc, selectedPlanType) => {
    if (!visibleColumns.includes('planType')) {
      return acc;
    }
    if (typeof(selectedPlanType.id) === 'number') {
      return acc.concat(planTypes.filter((planType) =>
        planType.id === selectedPlanType.id
      ));
    } else {
      let mappedPlanTypes = selectedPlanType.id.split(',');
      return acc.concat(planTypes.filter((planType) =>
        mappedPlanTypes.includes(planType.id.toString())
      ));
    }
  }, []);

  return {
    filteredPayers: visibleColumns.includes('payer') ? selectedPayers : [],
    filteredStates: visibleColumns.includes('states') ? selectedStates : [],
    filteredIndications: visibleColumns.includes('indications') ? selectedIndications : [],
    filteredPbms: visibleColumns.includes('pbm') ? selectedPbms : [],
    filteredPlanTypes,
    filteredProducts: selectedProducts,
  };
};

